import { useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { ApiError, del, post } from '../api/client';
import { keys, refreshSummaries, useApplications } from '../api/hooks';
import { ConfirmButton, useUI } from '../components/overlays';
import { Icon } from '../components/ui';

interface ImportReport {
  dryRun: boolean;
  format: 'prototype' | 'job-tracker';
  created: number;
  skipped: number;
  errors: { legacyId: string; message: string }[];
  settingsUpdated: boolean;
  readinessItems: number;
  resumes: number;
  assetsToReupload: string[];
}

function Report({ r }: { r: ImportReport }) {
  return (
    <div className="pform" role="status">
      <b>{r.dryRun ? 'Пробный прогон — ничего не записано' : 'Импорт выполнен'}</b>
      <ul style={{ margin: '8px 0 0', paddingLeft: 18 }}>
        <li>Формат: {r.format === 'prototype' ? 'экспорт прототипа' : 'экспорт трекера'}</li>
        <li>
          {r.dryRun ? 'Будет создано' : 'Создано'} вакансий: {r.created}
        </li>
        <li>Пропущено (уже импортированы): {r.skipped}</li>
        {r.settingsUpdated && <li>Настройки {r.dryRun ? 'будут обновлены' : 'обновлены'}</li>}
        {r.readinessItems > 0 && <li>Пунктов чек-листа: {r.readinessItems}</li>}
        {r.resumes > 0 && <li>Резюме-ссылок: {r.resumes}</li>}
      </ul>
      {r.errors.length > 0 && (
        <>
          <p style={{ margin: '8px 0 4px' }}>Ошибки ({r.errors.length}):</p>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {r.errors.map((e) => (
              <li key={e.legacyId} className="sm">
                {e.legacyId}: {e.message}
              </li>
            ))}
          </ul>
        </>
      )}
      {r.assetsToReupload.length > 0 && (
        <>
          <p style={{ margin: '8px 0 4px' }}>
            Эти файлы хранились в Claude и не переносятся — загрузите их заново:
          </p>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {r.assetsToReupload.map((a) => (
              <li key={a} className="sm">
                {a}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

/** Данные: экспорт, импорт (сначала пробный прогон), демо-данные. */
export function DataBox() {
  const ui = useUI();
  const qc = useQueryClient();
  const apps = useApplications();
  const input = useRef<HTMLInputElement>(null);
  const [pending, setPending] = useState<{ json: unknown; name: string } | null>(null);
  const [report, setReport] = useState<ImportReport | null>(null);
  const hasDemo = (apps.data ?? []).some((a) => a.isDemo);
  const refresh = async () => {
    await qc.invalidateQueries();
    refreshSummaries(qc);
  };

  const pick = async (file: File | undefined) => {
    if (!file) return;
    try {
      const json = JSON.parse(await file.text()) as unknown;
      const r = await post<ImportReport>('/import?dryRun=true', json);
      setPending({ json, name: file.name });
      setReport(r);
    } catch (e) {
      ui.toast(e instanceof ApiError ? e.message : 'Файл не похож на экспорт трекера');
    }
  };

  return (
    <section className="box" id="data">
      <h2>Данные</h2>
      <p className="muted sm" style={{ marginTop: -6 }}>
        Вакансий: {apps.data?.length ?? '…'}. Экспорт — весь JSON, его можно импортировать обратно. Импорт
        принимает и экспорт прототипа («Экспорт в JSON»); повторный импорт того же файла не создаёт дублей.
      </p>
      <div className="btnrow">
        <a className="btn sm" href="/api/export">
          <Icon name="download" size={15} /> Экспорт в JSON
        </a>
        <button className="btn sm" onClick={() => input.current?.click()}>
          <Icon name="upload" size={15} /> Импорт из JSON
        </button>
        <input
          ref={input}
          type="file"
          accept="application/json,.json"
          hidden
          onChange={(e) => {
            void pick(e.target.files?.[0]);
            e.target.value = '';
          }}
        />
        {hasDemo ? (
          <ConfirmButton
            className="btn sm danger"
            onConfirm={async () => {
              await del('/demo');
              await refresh();
              ui.toast('Демо-данные убраны');
            }}
          >
            Убрать демо-данные
          </ConfirmButton>
        ) : (
          <button
            className="btn sm ghost"
            onClick={async () => {
              await post('/demo');
              await qc.invalidateQueries({ queryKey: keys.apps() });
              refreshSummaries(qc);
              ui.toast('Добавлены демо-данные');
            }}
          >
            Добавить демо-данные
          </button>
        )}
      </div>
      {report && <Report r={report} />}
      {pending && report?.dryRun && (
        <div className="btnrow" style={{ marginTop: 10 }}>
          <button
            className="btn primary"
            onClick={async () => {
              const r = await post<ImportReport>('/import', pending.json);
              setReport(r);
              setPending(null);
              await refresh();
              ui.toast(`Импортировано вакансий: ${r.created}`);
            }}
          >
            Импортировать «{pending.name}»
          </button>
          <button
            className="btn ghost"
            onClick={() => {
              setPending(null);
              setReport(null);
            }}
          >
            Отмена
          </button>
        </div>
      )}
    </section>
  );
}
